import { type FormEvent, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@apollo/client/react'
import { useLang } from '../../contexts/LangContext'
import { REQUEST_PASSWORD_RESET_MUTATION } from '../../graphql/auth'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import FormError from '../../components/ui/FormError'

interface RequestPasswordResetData {
  requestPasswordReset: {
    message: string
    errors: string[]
  }
}

export default function ForgotPassword() {
  const { t } = useLang()
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)
  const [serverErrors, setServerErrors] = useState<string[]>([])
  const [success, setSuccess] = useState(false)

  const [mutate, { loading }] = useMutation<RequestPasswordResetData>(REQUEST_PASSWORD_RESET_MUTATION)

  function validateEmail(value: string) {
    if (!value) return t('Το email είναι υποχρεωτικό', 'Email is required')
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return t('Μη έγκυρο email', 'Invalid email')
    return ''
  }

  function handleBlur() {
    setEmailTouched(true)
    setEmailError(validateEmail(email))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const err = validateEmail(email)
    if (err) {
      setEmailError(err)
      setEmailTouched(true)
      return
    }

    setServerErrors([])
    try {
      const { data } = await mutate({ variables: { email } })
      const result = data?.requestPasswordReset
      if (!result) return

      if (result.errors.length) {
        setServerErrors(result.errors)
        return
      }

      setSuccess(true)
    } catch {
      setServerErrors([t('Κάτι πήγε στραβά. Δοκιμάστε ξανά.', 'Something went wrong. Please try again.')])
    }
  }

  if (success) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary-100 text-secondary-600 mb-6">
            <svg width="32" height="32" fill="none" viewBox="0 0 24 24">
              <path d="M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {t('Ελέγξτε το email σας', 'Check your email')}
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            {t(
              `Αν υπάρχει λογαριασμός με το email ${email}, θα λάβετε οδηγίες επαναφοράς κωδικού.`,
              `If an account exists for ${email}, you will receive password reset instructions.`
            )}
          </p>
          <Link
            to="/signin"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:underline"
          >
            ← {t('Επιστροφή στη σύνδεση', 'Back to Sign In')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900">
            {t('Επαναφορά κωδικού', 'Reset Password')}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {t(
              'Εισάγετε το email σας και θα σας στείλουμε οδηγίες επαναφοράς.',
              "Enter your email and we'll send you reset instructions."
            )}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <FormError errors={serverErrors} />

            <Input
              type="email"
              label="Email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={handleBlur}
              error={emailTouched ? emailError : ''}
              required
              autoComplete="email"
            />

            <Button type="submit" loading={loading} fullWidth size="lg">
              {t('Αποστολή οδηγιών', 'Send Instructions')}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          <Link to="/signin" className="text-primary-600 font-medium hover:underline">
            ← {t('Επιστροφή στη σύνδεση', 'Back to Sign In')}
          </Link>
        </p>
      </div>
    </div>
  )
}
